import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building,
  Tag,
  Send,
  Ticket,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Timeline } from '@/components/ui/Timeline';
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { SentimentIndicator } from '@/components/modules/SentimentIndicator';
import { useContactsStore } from '@/stores/contactsStore';
import { api, fromUnix } from '@/lib/api';
import type { ApiResponse } from '@nexus/shared/types';

interface TimelineItem {
  id: string;
  type: string;
  title: string;
  summary: string;
  contactName?: string;
  timestamp: string;
}

interface ContextEdge {
  id: string;
  name: string;
  type: string;
  edge: string;
}

export function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selected: contact, isLoading, error, fetchOne, remove, clearSelected } = useContactsStore();

  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [connectedEntities, setConnectedEntities] = useState<ContextEdge[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch contact on mount
  useEffect(() => {
    if (id) {
      fetchOne(id);
    }
    return () => {
      clearSelected();
    };
  }, [id]);

  // Fetch timeline
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setTimelineLoading(true);
    api
      .get<ApiResponse<TimelineItem[]>>(`/api/contacts/${id}/timeline`)
      .then((res) => {
        if (cancelled) return;
        const items = (res.data ?? []).map((item) => ({
          ...item,
          timestamp: typeof item.timestamp === 'number'
            ? fromUnix(item.timestamp as unknown as number)
            : item.timestamp,
        }));
        setTimeline(items);
      })
      .catch(() => {
        if (!cancelled) setTimeline([]);
      })
      .finally(() => {
        if (!cancelled) setTimelineLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  // Fetch connected entities from context graph
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setEntitiesLoading(true);
    api
      .get<ApiResponse<{ edges: ContextEdge[] }>>(`/api/context/contact/${id}`)
      .then((res) => {
        if (cancelled) return;
        setConnectedEntities(res.data?.edges ?? []);
      })
      .catch(() => {
        if (!cancelled) setConnectedEntities([]);
      })
      .finally(() => {
        if (!cancelled) setEntitiesLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    setDeleteLoading(true);
    try {
      await remove(id);
      navigate('/contacts');
    } catch {
      /* handled by store */
    } finally {
      setDeleteLoading(false);
    }
  };

  if (isLoading && !contact) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        {/* Back link skeleton */}
        <Skeleton className="h-4 w-32" />
        {/* Profile header skeleton */}
        <div className="bg-surface-800 border border-surface-700 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <Skeleton className="h-14 w-14 rounded-full" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-6 w-48" />
              <div className="flex items-center gap-4">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-32" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
            </div>
          </div>
          {/* Score cards skeleton */}
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-surface-700">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i}>
                <Skeleton className="h-3 w-20 mb-2" />
                <Skeleton className="h-4 w-full rounded-full" />
              </div>
            ))}
          </div>
        </div>
        {/* Content grid skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
              <Skeleton className="h-4 w-40 mb-4" />
              <SkeletonText lines={5} />
            </div>
          </div>
          <div className="space-y-6">
            <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
              <Skeleton className="h-4 w-32 mb-4" />
              <SkeletonText lines={3} />
            </div>
            <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
              <Skeleton className="h-4 w-32 mb-4" />
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!contact && !isLoading) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        <Link to="/contacts" className="inline-flex items-center gap-1.5 text-sm text-surface-400 hover:text-surface-200 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Contacts
        </Link>
        <EmptyState
          icon={<AlertCircle className="h-8 w-8" />}
          title="Contact not found"
          description={error ?? 'The contact you are looking for does not exist or could not be loaded.'}
          action={
            <Button variant="secondary" onClick={() => navigate('/contacts')}>
              Back to Contacts
            </Button>
          }
        />
      </div>
    );
  }

  const leadScore = contact!.leadScore ?? 0;
  const churnRisk = contact!.churnRisk ?? 0;

  return (
    <div className="space-y-6 max-w-[1440px]">
      {/* Back link */}
      <Link to="/contacts" className="inline-flex items-center gap-1.5 text-sm text-surface-400 hover:text-surface-200 transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Back to Contacts
      </Link>

      {/* Profile Header */}
      <div className="bg-surface-800 border border-surface-700 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-nexus-700 flex items-center justify-center text-lg font-bold text-nexus-200 flex-shrink-0">
              {contact!.name.charAt(0)}
            </div>
            <div>
              <h1 className="text-xl font-bold text-surface-100">{contact!.name}</h1>
              <div className="flex items-center gap-4 mt-2 flex-wrap">
                {contact!.email && (
                  <div className="flex items-center gap-1.5 text-sm text-surface-400">
                    <Mail className="h-3.5 w-3.5" />
                    {contact!.email}
                  </div>
                )}
                {contact!.phone && (
                  <div className="flex items-center gap-1.5 text-sm text-surface-400">
                    <Phone className="h-3.5 w-3.5" />
                    {contact!.phone}
                  </div>
                )}
                {contact!.companyId && (
                  <div className="flex items-center gap-1.5 text-sm text-surface-400">
                    <Building className="h-3.5 w-3.5" />
                    {contact!.companyId}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Badge variant="info">{contact!.type}</Badge>
                {contact!.sentiment && (
                  <SentimentIndicator sentiment={contact!.sentiment} showLabel />
                )}
                {(contact!.tags ?? []).map((tag) => (
                  <Badge key={tag} variant="default">
                    <Tag className="h-2.5 w-2.5 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" icon={<Send className="h-4 w-4" />} size="sm" onClick={() => navigate('/email')}>
              Send Email
            </Button>
            <Button variant="secondary" icon={<Ticket className="h-4 w-4" />} size="sm" onClick={() => navigate('/support')}>
              Create Ticket
            </Button>
            <Button variant="danger" icon={<Trash2 className="h-4 w-4" />} size="sm" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          </div>
        </div>

        {/* Score cards */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-surface-700">
          <div>
            <p className="text-xs text-surface-500">Lead Score</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-2 bg-surface-700 rounded-full overflow-hidden">
                <div className="h-full bg-nexus-500 rounded-full" style={{ width: `${leadScore}%` }} />
              </div>
              <span className="text-sm font-bold text-surface-200">{leadScore}</span>
            </div>
          </div>
          <div>
            <p className="text-xs text-surface-500">Churn Risk</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-2 bg-surface-700 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${churnRisk}%` }} />
              </div>
              <span className="text-sm font-bold text-emerald-400">{churnRisk}%</span>
            </div>
          </div>
          <div>
            <p className="text-xs text-surface-500">Total Interactions</p>
            <p className="text-sm font-bold text-surface-200 mt-1">{contact!.totalInteractions}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Timeline */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-surface-200 mb-4">Interaction Timeline</h3>
            {timelineLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-nexus-500 border-t-transparent rounded-full" />
              </div>
            ) : timeline.length > 0 ? (
              <Timeline items={timeline} />
            ) : (
              <p className="text-sm text-surface-500 text-center py-8">No interactions recorded yet</p>
            )}
          </div>
        </div>

        {/* Right: Relationships & Chart */}
        <div className="space-y-6">
          {/* Relationships */}
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-surface-200 mb-4">Relationships</h3>
            {entitiesLoading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin h-5 w-5 border-2 border-nexus-500 border-t-transparent rounded-full" />
              </div>
            ) : connectedEntities.length > 0 ? (
              <div className="space-y-2">
                {connectedEntities.map((entity) => (
                  <div
                    key={entity.id}
                    className="flex items-center justify-between px-3 py-2.5 bg-surface-900/50 rounded-lg border border-surface-700/50"
                  >
                    <div className="flex items-center gap-2">
                      {entity.type === 'company' ? (
                        <Building className="h-4 w-4 text-blue-400" />
                      ) : (
                        <Mail className="h-4 w-4 text-purple-400" />
                      )}
                      <span className="text-sm text-surface-200">{entity.name}</span>
                    </div>
                    <Badge variant="default">{entity.edge.replace(/_/g, ' ')}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-surface-500 text-center py-4">No relationships found</p>
            )}
          </div>

          {/* Sentiment Trend */}
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-surface-200 mb-4">Sentiment Trend</h3>
            <p className="text-sm text-surface-500 text-center py-8">Not enough interaction data for trend analysis</p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete Contact"
        description={`Are you sure you want to delete ${contact!.name}? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}
